import { Box, Button, Chip, FormControlLabel, FormGroup, Stack, Switch, Tooltip, Typography } from '@mui/material'

import { CustomTooltip } from '@hi/brabus'

import { useTheme } from '@mui/material/styles'
import { CONVERSION_EVENTS_KEYS, CONVERSION_EVENTS_OBJECT, CONVERSION_REPORT_ROUTES } from 'constants/index'
import { useNavigate } from 'react-router-dom'
import { useCallback, useMemo } from 'react'
import { sortObjectList } from 'utils/sortObject'
import useMetaEventsStatusUpdate from 'mutations/useMetaEventsStatusUpdate'
import useSnackbar from 'hooks/useSnackbar'
import Grid from '../Grid'

/**
 * @typedef {Object} DataGridProps
 * @property {string} keyword
 * @property {Array} data
 * @param {DataGridProps} props
 * @returns
 */
function CustomDataGrid({ keyword, data }) {
  const theme = useTheme()
  const navigate = useNavigate()
  const { createSnackbar } = useSnackbar()

  const { mutateAsync, isLoading, isError, variables } = useMetaEventsStatusUpdate()

  const { enabled, enabledNotActived, notEnabled } = useMemo(() => {
    let value = { enabled: [], enabledNotActived: [], notEnabled: [] }

    if (data?.length) {
      value = data.reduce((previous, current) => {
        if (Object.prototype.hasOwnProperty.call(current, 'metaEventId')) {
          if (!current.isActive) {
            previous.enabledNotActived.push(current)
          } else {
            previous.enabled.push(current)
          }
        } else {
          previous.notEnabled.push(current)
        }

        return previous
      }, value)
    }

    return {
      enabled: sortObjectList('name', value.enabled),
      enabledNotActived: sortObjectList('name', value.enabledNotActived),
      notEnabled: sortObjectList('name', value.notEnabled),
    }
  }, [data])

  const handleSwitch = useCallback(
    async ({ status, metaEventId }) => {
      try {
        await mutateAsync({ meta_event_id: metaEventId, is_active: status })
      } catch (error) {
        console.log(error)
      } finally {
        if (isError) {
          createSnackbar('Não foi possível atualizar o status. Tente novamente!')
        }
      }
    },
    [createSnackbar, isError, mutateAsync],
  )

  const columns = [
    {
      field: 'isActive',
      sortable: true,
      flex: 1,
      width: 100,
      renderHeader: () => (
        <CustomTooltip
          title={
            <Stack p={1}>
              <Typography variant="inherit" fontWeight="bold">
                Status
              </Typography>
            </Stack>
          }
          bordercolor="#DADDE9"
          bgcolor="#F5F5F9"
          placement="top"
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              bgcolor: 'none',
              gap: '0.25rem',
            }}
          >
            <Typography variant="inherit" fontWeight="bold">
              Status
            </Typography>
          </Box>
        </CustomTooltip>
      ),
      renderCell: ({ row }) => {
        const disabledRow =
          (isLoading && variables?.meta_event_id === row.metaEventId) ||
          !Object.prototype.hasOwnProperty.call(row, 'metaEventId')

        return (
          <Stack sx={{ width: '100%', flexDirection: 'row', alignItems: 'center', gap: '1rem', cursor: 'pointer' }}>
            <Tooltip placement="left" title={CONVERSION_EVENTS_OBJECT[keyword].title}>
              {CONVERSION_EVENTS_OBJECT[keyword].icon(
                row.isActive ? theme.palette.colors.icon : theme.palette.colors.disabled,
              )}
            </Tooltip>
            <FormGroup>
              <FormControlLabel
                sx={{
                  color: row.isActive ? '#333' : theme.palette.colors.disabled,
                }}
                control={
                  <Switch
                    size="small"
                    checked={row.isActive}
                    onChange={(event) => handleSwitch({ status: event.target.checked, metaEventId: row.metaEventId })}
                  />
                }
                label={row.isActive ? 'Ativo' : 'Inativo'}
                disabled={disabledRow}
              />
            </FormGroup>
          </Stack>
        )
      },
    },
    {
      field: 'name',
      sortable: true,
      flex: 1,
      minWidth: 100,
      renderHeader: () => (
        <CustomTooltip
          title={
            <Stack p={1}>
              <Typography variant="inherit" fontWeight="bold">
                Nome do evento
              </Typography>
            </Stack>
          }
          bordercolor="#DADDE9"
          bgcolor="#F5F5F9"
          placement="top"
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              bgcolor: 'none',
              gap: '0.25rem',
            }}
          >
            <Typography variant="inherit" fontWeight="bold">
              Nome do evento
            </Typography>
          </Box>
        </CustomTooltip>
      ),
      renderCell: ({ row }) => {
        return (
          <Stack flexDirection="column" sx={{ width: '100%' }}>
            <CustomTooltip title={row.name}>
              <Typography
                variant="body1"
                sx={{
                  textWrap: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  color: row.isActive ? '#333' : '#BEBEBE',
                }}
              >
                {row.name}
              </Typography>
            </CustomTooltip>
          </Stack>
        )
      },
    },
    {
      field: 'metaEventName',
      sortable: false,
      flex: 1,
      minWidth: 100,
      renderHeader: () => (
        <CustomTooltip title="Evento na Meta">
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              bgcolor: 'none',
              gap: '0.25rem',
            }}
          >
            <Typography variant="inherit" fontWeight="bold" whiteSpace="break-spaces" lineHeight="1.2rem">
              Evento na Meta
            </Typography>
          </Box>
        </CustomTooltip>
      ),
      renderCell: ({ row }) => (
        <Box flex={1} display="flex" alignItems="center">
          <CustomTooltip title={row.metaEventName}>
            <Chip
              size="small"
              variant="contained"
              label={row.metaEventName}
              sx={{
                maxWidth: '11rem',
                textWrap: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                color: row.isActive ? '#333' : '#BEBEBE',
              }}
            />
          </CustomTooltip>
        </Box>
      ),
    },
    {
      field: 'channels',
      sortable: false,
      flex: 1,
      minWidth: 100,
      renderHeader: () => (
        <CustomTooltip title="Canais Hi">
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              bgcolor: 'none',
              gap: '0.25rem',
            }}
          >
            <Typography variant="inherit" fontWeight="bold">
              Canais Hi
            </Typography>
          </Box>
        </CustomTooltip>
      ),
      renderCell: ({ row }) => {
        return (
          <Box flex={1} display="flex" align="center">
            <Typography
              sx={{
                color: row.isActive ? '#333' : '#BEBEBE',
              }}
            >
              {row.channels || '-'}
            </Typography>
          </Box>
        )
      },
    },
    {
      field: 'reportType',
      sortable: false,
      flex: 1,
      minWidth: 100,
      renderHeader: () => (
        <CustomTooltip title="Tipos de relatório">
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              bgcolor: 'none',
              gap: '0.25rem',
            }}
          >
            <Typography variant="inherit" fontWeight="bold">
              Tipos de relatório
            </Typography>
          </Box>
        </CustomTooltip>
      ),
      renderCell: ({ row }) => (
        <Box flex={1} display="flex" align="center">
          <Typography
            sx={{
              color: row.isActive ? '#333' : '#BEBEBE',
            }}
          >
            {row.reportType || '-'}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'actions',
      sortable: false,
      headerName: '',
      flex: 1,
      width: 100,
      align: 'center',
      renderHeader: () => (
        <Typography variant="inherit" fontWeight="bold">
          Ações
        </Typography>
      ),
      renderCell: ({ row }) => (
        <Box flex={1} display="flex" align="center" justifyContent="center">
          <Button
            variant={Object.prototype.hasOwnProperty.call(row, 'metaEventId') ? 'outlined' : 'contained'}
            sx={{
              display: 'flex',
              alignItems: 'center',
              bgcolor: !Object.prototype.hasOwnProperty.call(row, 'metaEventId')
                ? theme.palette.colors.buttons.container.background
                : 'transparent',
              color: !Object.prototype.hasOwnProperty.call(row, 'metaEventId')
                ? theme.palette.colors.buttons.container.color
                : theme.palette.info.main,
              ':hover': {
                bgcolor: !Object.prototype.hasOwnProperty.call(row, 'metaEventId')
                  ? theme.palette.colors.buttons.container.hover
                  : 'transparent',
                color: !Object.prototype.hasOwnProperty.call(row, 'metaEventId')
                  ? theme.palette.common.white
                  : theme.palette.info.main,
                border: !Object.prototype.hasOwnProperty.call(row, 'metaEventId') ? 'none' : '1px solid',
              },
            }}
            onClick={() => {
              navigate(
                `/${CONVERSION_REPORT_ROUTES.MANAGEMENT_EVENTS}/${row.id}/${CONVERSION_EVENTS_KEYS[keyword]}/${
                  Object.prototype.hasOwnProperty.call(row, 'metaEventId') ? 'edit' : 'enable'
                }`,
                {
                  replace: true,
                  state: {
                    name: row.name || '',
                    metaEventId: row.metaEventId || '',
                  },
                },
              )
            }}
          >
            {Object.prototype.hasOwnProperty.call(row, 'metaEventId') ? 'Editar' : 'Habilitar'}
          </Button>
        </Box>
      ),
    },
  ]

  return (
    <Box display="flex" mt="1rem" flexDirection="column" gap="1.5rem" sx={{ borderRadius: '4px', masHeight: '50rem' }}>
      <Typography
        sx={{
          fontSize: '1.5rem',
          fontWeight: 700,
          lineHeight: '1.5rem',
        }}
      >
        {CONVERSION_EVENTS_OBJECT[keyword].title}
      </Typography>
      <Grid
        columns={columns}
        rows={[...enabled, ...enabledNotActived, ...notEnabled]}
        getRowId={(el) => el.id}
        pageSizeOptions={[10, 25, 50, 100]}
      />
    </Box>
  )
}

export default CustomDataGrid
